import { getDef, isSecret, type SettingValue } from './settings-schema.ts';
import type { PublicPlans } from './api.ts';

/**
 * 游客模式的几件纯逻辑：登录后回哪儿、草稿怎么留、游客按什么额度看。
 *
 * 全是纯函数（存储从参数传进来），所以能在 node 里测，不用浏览器。
 */

/* ══════════════════════ 登录后回哪儿 ══════════════════════ */

/** 登录后默认去哪一页。总览是每天真正会盯的那一屏 */
export const DEFAULT_AFTER_LOGIN = '/p/home';

/**
 * 从 `?next=` 里取出登录后要回的路径。**只认站内路径。**
 *
 * ════════════════════════════════════════════════════════
 * 这是一个「开放重定向」的口子，必须堵死
 * ════════════════════════════════════════════════════════
 * 如果 `next` 什么都认，有人就能发一个
 * `jyfkws.com/login?next=https://jysh-online.xyz/` 的链接：
 * 用户在我们的真网站上登录，登录完被送到一个长得一模一样的假站，
 * 假站再说「会话过期，请重新输入验证码」—— 他会信，因为上一步确实是我们的站。
 * 对一个链上工具来说，下一步就是「请连接钱包」。
 *
 * 所以只放行以单个 `/` 开头的站内路径：
 *   `//evil.com`   浏览器会当成协议相对地址 → 拒绝
 *   `/\evil.com`   部分浏览器把反斜杠当斜杠 → 拒绝
 *   `https://…`    → 拒绝
 *   `/login…`      回到登录页会绕圈 → 拒绝
 * 认不出的一律回默认页，不报错 —— 用户不需要知道有人试过什么。
 */
export function safeNext(search: string): string {
  let raw: string | null = null;
  try {
    raw = new URLSearchParams(search).get('next');
  } catch {
    raw = null;
  }
  if (raw === null) return DEFAULT_AFTER_LOGIN;
  const p = raw.trim();
  if (!p.startsWith('/')) return DEFAULT_AFTER_LOGIN;
  if (p.startsWith('//') || p.startsWith('/\\')) return DEFAULT_AFTER_LOGIN;
  /* 控制字符和空白：浏览器解析 URL 时会把制表、换行剥掉，
     于是 `/\t/evil.com` 会变成 `//evil.com` */
  if (/[\u0000-\u001f\u007f\s]/.test(p)) return DEFAULT_AFTER_LOGIN;
  if (p === '/login' || p.startsWith('/login?') || p.startsWith('/login/')) return DEFAULT_AFTER_LOGIN;
  return p;
}

/** 去登录页、登录完回到 `back` 这一页的链接 */
export function loginHref(back: string): string {
  const b = back && back !== '/' ? back : DEFAULT_AFTER_LOGIN;
  return '/login?next=' + encodeURIComponent(b);
}

/* ══════════════════════ 游客的草稿：登录回来还在 ══════════════════════ */

/**
 * 游客在设置页改了一堆，点保存 → 去登录 → 回来。**那一堆改动不能丢。**
 *
 * 登录会把整个应用重新拉一遍（设置、凭据、外观都要换成他账号里的），
 * 页面随之重新挂载，页面上的草稿也就没了。所以先把草稿放进 sessionStorage，
 * 登录回到那一页时再放回去 —— **放回草稿，不自动保存**：
 * 他可能是老用户，账号里本来就配过，自动保存会把那些覆盖掉。
 * 回来之后他看一眼、再点一次保存，是最不会出错的。
 *
 * 用 sessionStorage 不用 localStorage：草稿是这一次浏览的东西，
 * 标签页关了就该没了，不该在这台电脑上留到下一次、下一个人。
 */
export const DRAFT_KEY = 'jyfk.guestDraft';

/** 草稿留多久。登录一般一两分钟；两小时之后还没回来，说明他已经不打算要了 */
export const DRAFT_TTL_MS = 2 * 3600_000;

/** 只用到 Storage 的这三个方法。测试里传一个 Map 做的假存储 */
export interface KV {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface Stash {
  v: 1;
  pages: Record<string, { at: number; changes: Record<string, SettingValue> }>;
}

function readStash(storage: KV): Stash {
  try {
    const raw = JSON.parse(storage.getItem(DRAFT_KEY) ?? 'null') as unknown;
    if (raw && typeof raw === 'object' && (raw as { v?: unknown }).v === 1
      && typeof (raw as { pages?: unknown }).pages === 'object' && (raw as { pages?: unknown }).pages !== null) {
      return raw as Stash;
    }
  } catch {
    /* 坏了就当没有 */
  }
  return { v: 1, pages: {} };
}

function writeStash(storage: KV, s: Stash): void {
  try {
    if (Object.keys(s.pages).length === 0) storage.removeItem(DRAFT_KEY);
    else storage.setItem(DRAFT_KEY, JSON.stringify(s));
  } catch {
    /* 隐身模式或存储满了。草稿留不住是浏览器的限制，不该让保存按钮报错 */
  }
}

/**
 * 只留这一页真正能存的那些键。
 *
 * **凭据一律不留。** 游客模式下凭据框本来就是锁着的，
 * 但就算哪天有人把它放开了，明文的 Bot Token 也绝不能进浏览器存储 ——
 * 那等于写在这台电脑的硬盘上，任何能打开开发者工具的人都看得到。
 * 不属于这一页的键、不落库的展示项也丢掉：回放时不该往草稿里塞来路不明的东西。
 */
function keepable(pageId: string, changes: Readonly<Record<string, SettingValue>>): Record<string, SettingValue> {
  const out: Record<string, SettingValue> = {};
  for (const [k, v] of Object.entries(changes)) {
    if (isSecret(k)) continue;
    const def = getDef(k);
    if (!def || def.page !== pageId || !def.stored || def.secret) continue;
    out[k] = v;
  }
  return out;
}

/** 把这一页的草稿留下来。空草稿等于清掉这一页的记录 */
export function stashGuestDraft(
  storage: KV, pageId: string, changes: Readonly<Record<string, SettingValue>>, now: number
): number {
  const s = readStash(storage);
  const kept = keepable(pageId, changes);
  if (Object.keys(kept).length === 0) delete s.pages[pageId];
  else s.pages[pageId] = { at: now, changes: kept };
  writeStash(storage, s);
  return Object.keys(kept).length;
}

/**
 * 取出这一页留着的草稿，**取完就删**。过期的顺手清掉。
 *
 * 取完就删是为了「只放回一次」：放回之后他点了放弃，
 * 下次再进这一页不该又冒出来。
 */
export function takeGuestDraft(storage: KV, pageId: string, now: number): Record<string, SettingValue> | null {
  const s = readStash(storage);
  let changed = false;
  for (const [id, p] of Object.entries(s.pages)) {
    if (!p || typeof p.at !== 'number' || now - p.at > DRAFT_TTL_MS || now < p.at - 60_000) {
      delete s.pages[id];
      changed = true;
    }
  }
  const hit = s.pages[pageId];
  if (hit) {
    delete s.pages[pageId];
    changed = true;
  }
  if (changed) writeStash(storage, s);
  if (!hit) return null;
  /* 再过一遍白名单：存储里的东西可能被别的版本写过，也可能被人手改过 */
  const kept = keepable(pageId, hit.changes ?? {});
  return Object.keys(kept).length > 0 ? kept : null;
}

/* ══════════════════════ 游客按什么额度看 ══════════════════════ */

/**
 * 游客看到的配额锁按**免费版**算。
 *
 * 不给的话游客模式下什么都不锁：他把价格刷新调到 60 秒（那是 VIP 的），
 * 注册完一保存，那一行报「超出你当前档位的上限」—— 前面看到的和后面拿到的不一样。
 * 按免费版显示，他在游客模式下就能看到「VIP 才能设到 60 秒」，那是实话。
 */
export function freeQuotas(plans: PublicPlans | null): Record<string, number> | undefined {
  if (!plans) return undefined;
  const out: Record<string, number> = {};
  for (const q of plans.quotas) out[q.key] = q.free;
  return out;
}
